import { isServer, html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { trustedCSS } from '../../lib/css/css.js';
import { DeepSlotChangeController } from '../../lib/deep-slot-change-controller/deep-slot-change-controller.js';
import { LinkSlottedFormElementsController } from '../../lib/link-slotted-form-elements/link-slotted-form-elements.js';
import { LINKED_SLOTTED_ELEMENTS_EVENT_TYPE } from '../../lib/link-slotted-form-elements/linked-slotted-elements-event.js';
import checkableFormControlGroupStyles from './checkable-form-control-group.css.js';

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
const CrntCheckableFormControlGroupMixin = (superClass, options) => {
  class CrntCheckableFormControlGroup extends superClass {
    constructor() {
      super(...arguments);
      this.disableAutoLink = false;
      this._checkableFormControlSelector = options.checkableFormControlSelector;
      this._checkableFormControlSelectorBordered = options.checkableFormControlSelectorBorderedSelector;
      this._linkSlottedFormElementsController = new LinkSlottedFormElementsController({
        enabled: false,
        host: this,
        selectors: options.linkSlottedFormElementsControllerOptions.selectors
      });
      this._deepSlotChangeController = new DeepSlotChangeController({
        callback: (mutations) => this._linkSlottedFormElementsController.deepSlotChangeCallback(mutations),
        host: this,
        slotNames: ["legend", "help", "validation"]
      });
    }
    static {
      this.styles = [trustedCSS(checkableFormControlGroupStyles)];
    }
    get _borderedCheckableFormControlGroups() {
      if (!isServer) {
        return Array.from(this.querySelectorAll(this._checkableFormControlSelectorBordered));
      }
      return [];
    }
    get _checkableFormControls() {
      if (!isServer) {
        return Array.from(this.querySelectorAll(this._checkableFormControlSelector));
      }
      return [];
    }
    get _containerClasses() {
      return classMap({
        "crnt-checkable-form-control-group": true,
        "crnt-checkable-form-control-group--bordered": this._borderedCheckableFormControlGroups.length > 0
      });
    }
    connectedCallback() {
      super.connectedCallback();
      this._connect();
    }
    disconnectedCallback() {
      super.disconnectedCallback();
      this._disconnect();
    }
    render() {
      return html`
				<div class="${this._containerClasses}" part="container" role="group" aria-labelledby="legend">
					<slot id="legend" @slotchange=${this._handleSlotChange} name="legend"></slot>
					<slot @slotchange=${this._handleSlotChange} name="help"></slot>
					<slot @slotchange=${this._handleSlotChange} name="validation"></slot>
					<div class="crnt-checkable-form-control-group__controls" part="controls">
						<slot @slotchange=${this._handleDefaultSlotChange}></slot>
					</div>
				</div>
			`;
    }
    willUpdate(_changedProperties) {
      super.willUpdate(_changedProperties);
      if (_changedProperties.has("disableAutoLink")) {
        this._linkSlottedFormElementsController.enabled = !this.disableAutoLink;
      }
    }
    _connect() {
      this._linkSlottedFormElementsController.enabled = !this.disableAutoLink;
      this._checkableFormControls.forEach((checkableFormControl) => {
        checkableFormControl.addEventListener(
          LINKED_SLOTTED_ELEMENTS_EVENT_TYPE,
          () => {
            this._linkedSlottedElementsEventListenerOnce.bind(this);
          },
          { once: true }
        );
        checkableFormControl.addEventListener(
          LINKED_SLOTTED_ELEMENTS_EVENT_TYPE,
          this._linkedSlottedElementsEventListener.bind(this)
        );
      });
    }
    _disconnect() {
      this._checkableFormControls.forEach((checkableFormControl) => {
        checkableFormControl.removeEventListener(
          LINKED_SLOTTED_ELEMENTS_EVENT_TYPE,
          this._linkedSlottedElementsEventListenerOnce.bind(this)
        );
        checkableFormControl.removeEventListener(
          LINKED_SLOTTED_ELEMENTS_EVENT_TYPE,
          this._linkedSlottedElementsEventListener.bind(this)
        );
      });
    }
    _handleDefaultSlotChange() {
      this._disconnect();
      this._connect();
    }
    _handleSlotChange() {
      this._linkSlottedFormElementsController.handleSlotChange();
    }
    _linkedSlottedElementsEventListener() {
      this._linkSlottedFormElementsController.setAlteredIds();
      this._linkSlottedFormElementsController.linkSlottedElements();
    }
    _linkedSlottedElementsEventListenerOnce() {
      this._linkSlottedFormElementsController.setOriginalIds();
    }
  }
  __decorateClass([
    property({ attribute: "disable-auto-link", reflect: true, type: Boolean })
  ], CrntCheckableFormControlGroup.prototype, "disableAutoLink");
  return CrntCheckableFormControlGroup;
};

export { CrntCheckableFormControlGroupMixin };
